import z from "zod";

export type ReportCreateType = z.infer<typeof ReportCreateSchema>;
export type ReportUpdateType = z.infer<typeof ReportUpdateSchema>;

export type SnapshotType = {
  id: number;
  blueprint_id: number;
  outline_id: number | null;
  html: string;
  lexical_json: string | null;
  created_at: string;
  updated_at: string;
  blueprint: ReportType | null;
};

export const SnapshotSchema: z.ZodType<SnapshotType> = z.lazy(() =>
  z
    .object({
      id: z.number().int(),
      blueprint_id: z.number().int(),
      outline_id: z.number().int().nullable(),
      html: z.string(),
      lexical_json: z.string().nullable(),
      created_at: z.string().datetime({ offset: true }),
      updated_at: z.string().datetime({ offset: true }),
      blueprint: ReportSchema.nullable()
    })
    .passthrough()
);

export const SnapshotSaveRequestBody = z
  .object({
    outline_id: z.number().int().nullable(),
    html: z.string(),
    lexical_json: z.string().nullable()
  })
  .passthrough();

export type FileType = {
  id: number;
  user_id: number;
  object_id: string;
  name: string;
  bytes: number;
  mime_type: string;
  created_at: string;
  updated_at: string;
};

//https://github.com/colinhacks/zod/issues/3293
//このままだとidがなぜかoptionalになるので上のissueからtsconfigの設定を変えている
export const FileSchema: z.ZodType<FileType> = z.lazy(() =>
  z
    .object({
      id: z.number().int(),
      user_id: z.number().int(),
      object_id: z.string(),
      name: z.string(),
      bytes: z.number().int(),
      mime_type: z.string(),
      created_at: z.string().datetime({ offset: true }),
      updated_at: z.string().datetime({ offset: true })
    })
    .passthrough()
);

export const ReportFormats = z.enum(["standard", "polite"]);

// 新しいtoneのenum定義
export const ReportTone = z.enum([
  "professional",
  "casual",
  "straightforward",
  "confident",
  "friendly"
]);

//元のReportPromptSchema
// export const ReportPromptSchema: z.ZodType<ReportType> = z.lazy(() =>
//   z
//     .object({
//       id: z.number().int(),
//       user_id: z.number().int(),
//       title: z.string(),
//       pattern: z.enum(["generation", "revising_existing_report", "training_text", "upload_file"]),
//       prompt: z.string(),
//       paste_text: z.string(),
//       format: ReportFormats,
//       character_length: z.number().int(),
//       language: z.enum(["japanese", "english"]),
//       purpose: z.string(),
//       tone: ReportTone,
//       quality: z.enum(["beginner", "intermediate", "advanced"]),
//       status: z.enum(["revisable", "unrevisable", "deleted"]),
//       created_at: z.string().datetime({ offset: true }),
//       updated_at: z.string().datetime({ offset: true }),
//       star: z.boolean(),
//       files: z.array(FileSchema).nullable(),
//       latest_snapshot: SnapshotSchema.nullable(),
//       latest_outline: OutlineSchema.nullable()
//     })
//     .passthrough()
// );

export type ReportType = {
  id: number;
  user_id: number;
  title: string;
  pattern: "generation" | "revising_existing_report" | "training_text" | "upload_file";
  prompt: string | null;
  paste_text: string | null;
  format: "standard" | "polite" | null;
  character_length: number;
  language: "japanese" | "english";
  purpose: string;
  tone: "professional" | "casual" | "straightforward" | "confident" | "friendly";
  quality: "beginner" | "intermediate" | "advanced";
  status: "revisable" | "unrevisable" | "deleted";
  created_at: string;
  updated_at: string;
  trashed_at: string | null;
  star: boolean;
  files: Array<FileType> | null;
  latest_snapshot: SnapshotType | null;
  latest_outline: OutlineType | null;
};

export const ReportSchema = z.lazy(() =>
  z
    .object({
      id: z.number().int(),
      user_id: z.number().int(),
      title: z.string(),
      pattern: z.enum(["generation", "revising_existing_report", "training_text", "upload_file"]),
      prompt: z.string().nullable(), // nullableを追加
      paste_text: z.string().nullable(), // nullableを追加
      format: z.enum(["standard", "polite"]).nullable(), // ReportFormatsではなく直接定義し、nullableを追加
      character_length: z.number().int(),
      language: z.enum(["japanese", "english"]),
      purpose: z.string(),
      tone: ReportTone,
      quality: z.enum(["beginner", "intermediate", "advanced"]),
      status: z.enum(["revisable", "unrevisable", "deleted"]),
      created_at: z.string().datetime({ offset: true }),
      updated_at: z.string().datetime({ offset: true }),
      trashed_at: z.string().nullable(),
      star: z.boolean(),
      files: z.array(FileSchema).nullable(),
      latest_snapshot: SnapshotSchema.nullable(),
      latest_outline: OutlineSchema.nullable()
    })
    .passthrough()
);

export const ReportUpdateSchema = z
  .object({
    title: z.string(),
    prompt: z.string().nullable(),
    paste_text: z.string().nullable(),
    format: ReportFormats.nullable(),
    character_length: z.number().int(),
    language: z.enum(["japanese", "english"]),
    purpose: z.string(),
    tone: ReportTone,
    quality: z.enum(["beginner", "intermediate", "advanced"])
  })
  .passthrough();

export const ReportCreateSchema = z
  .object({
    title: z.string(),
    pattern: z.enum(["generation", "revising_existing_report", "training_text", "upload_file"]),
    prompt: z.string().nullable(),
    paste_text: z.string().nullable(),
    format: ReportFormats.nullable(),
    character_length: z.number().int(),
    language: z.enum(["japanese", "english"]),
    purpose: z.string(),
    tone: ReportTone,
    quality: z.enum(["beginner", "intermediate", "advanced"]),
    file_ids: z.array(z.string())
  })
  .passthrough();

// export const ReportSchema = z
//   .object({
//     id: z.number().int(),
//     blueprint_id: z.number().int(),
//     outline_id: z.number().int().nullable(),
//     html: z.string(),
//     lexical_json: z.string().nullable(),
//     created_at: z.string().datetime({ offset: true }),
//     updated_at: z.string().datetime({ offset: true }),
//     blueprint: ReportPromptSchema.nullable()
//   })
//   .passthrough();

export const ReportSaveSchema = z
  .object({
    outline_id: z.number().int().nullable(),
    html: z.string(),
    lexical_json: z.string().nullable()
  })
  .passthrough();

export type OutlineType = {
  id: number;
  blueprint_id: number;
  created_at: string;
  updated_at: string;
  blueprint: ReportType | null;
  sections: Array<SectionType>;
};

export const OutlineSchema: z.ZodType<OutlineType> = z.lazy(() =>
  z
    .object({
      id: z.number().int(),
      blueprint_id: z.number().int(),
      created_at: z.string().datetime({ offset: true }),
      updated_at: z.string().datetime({ offset: true }),
      blueprint: ReportSchema.nullable(),
      sections: z.array(SectionSchema)
    })
    .passthrough()
);

export const OutlineCreateRequestBody = z
  .object({
    sections: z.array(
      z
        .object({
          list_number: z.number().int(),
          content: z.string(),
          character_length: z.number().int()
        })
        .passthrough()
    )
  })
  .passthrough();

export type SectionType = {
  id: number;
  blueprint_id: number;
  outline_id: number;
  list_number: number;
  content: string;
  character_length: number;
  created_at: string;
  updated_at: string;
  blueprint: ReportType | null;
  // outline: OutlineType | null;
};

export const SectionSchema: z.ZodType<SectionType> = z.lazy(() =>
  z
    .object({
      id: z.number().int(),
      blueprint_id: z.number().int(),
      outline_id: z.number().int(),
      list_number: z.number().int(),
      content: z.string(),
      character_length: z.number().int(),
      created_at: z.string().datetime({ offset: true }),
      updated_at: z.string().datetime({ offset: true }),
      blueprint: ReportSchema.nullable()
      // outline: OutlineSchema.nullable()
    })
    .passthrough()
);
